import { Box, Typography } from '@mui/material';
import { MarkerF } from '@react-google-maps/api';
import dayjs from 'dayjs';
import type React from 'react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { useRecoilState } from 'recoil';
import type { OfferingList } from '../offering';
import * as blu1Png from '@/components/shared/image/blu-1.png';
import * as blu2Png from '@/components/shared/image/blu-2.png';
import * as blu3Png from '@/components/shared/image/blu-3.png';
import * as bluGrnSvg from '@/components/shared/image/blu-grn-pin.svg';
import * as bluSvg from '@/components/shared/image/blu-pin.svg';
import * as green1Png from '@/components/shared/image/green-1.png';
import * as green2Png from '@/components/shared/image/green-2.png';
import * as green3Png from '@/components/shared/image/green-3.png';
import * as grnSvg from '@/components/shared/image/grn-pin.svg';
import { MbDialog } from '@/components/shared/modules';
import { queryKeys } from '@/constants/query-key';
import { ApiNamePaths } from '@/constants/rest-api-name-path';
import GoogleMapWrapper from '@/features/map/components/googleMapWrapper';
import MyGoogleMap from '@/features/map/components/myGoogleMap';
import { useLandscapeResize } from '@/hooks/useLandscapeResize';
import { useGenericQuery } from '@/modules/hooks/use-query';
import { calculateMapProperties } from '@/modules/utils/coordinatesUtils';
import * as paths from '@/routes/paths';
import { settingState } from '@/state/settingState';

const STORYBOOK_FLAG_KEY = '__STORYBOOK__';

function formatDate(datetimeStr: string) {
  if (!datetimeStr) return '';

  return dayjs(datetimeStr).locale('ja').format('MM/DD(ddd)');
}

function convertTimeFormat(dateTimeString: string) {
  if (!dateTimeString) return '';

  return dayjs(dateTimeString).format('HH:mm');
}

type InputDataType = {
  areaId: number;
  searchType: string;
  checkedOfferingList: OfferingList[];
};

type OfferingsAcceptabilityResponse = {
  optimizedRoutes: string;
};

export const WaypointConfirmPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const [setting] = useRecoilState(settingState);
  const isStorybook = Boolean((globalThis as Record<string, unknown>)[STORYBOOK_FLAG_KEY]);

  const { areaId, searchType, checkedOfferingList } = location?.state as InputDataType;
  const [modalShow, setModalShow] = useState<boolean>(false);
  const [center, setCenter] = useState<google.maps.LatLngLiteral | undefined>({ lat: 0, lng: 0 });
  const { landscapeSplitStyle, setIsLandscapeResizing } = useLandscapeResize();
  const [currentLocation, setCurrentLocation] = useState<google.maps.LatLngLiteral | undefined>(undefined);
  const [reservationIds, setReservationIds] = useState<string>('');
  const [fetchEnable, setFetchEnable] = useState<boolean>(false);

  // 縮尺を取得する
  const [zoom, setCurrentZoom] = useState<number>(0);

  // ドライバID（cognitoのdriverId）
  const driverId = setting?.driverId || '';

  // オファリング一覧画面に遷移する
  const navigateToOfferingPage = () => {
    navigate(paths.offering, { state: { areaId, searchType, checkedOfferingList } });
  };

  // ポップアップのOKボタン
  const handleClosePopup = () => {
    setModalShow(false);
    // オファリング一覧画面に遷移する
    navigateToOfferingPage();
  };

  // オファリング受託可否確認APIAPI
  const {
    data: offeringsAcceptabilityResponse,
    isError,
    isLoading,
  } = useGenericQuery<OfferingsAcceptabilityResponse>(
    queryKeys.v1Offerings,
    ApiNamePaths.offeringsAcceptabilityApi.apiName,
    `${ApiNamePaths.offeringsAcceptabilityApi.apiPath}/${reservationIds}`,
    { enabled: fetchEnable },
    {
      queryParams: {
        driverId,
        latitude: currentLocation ? currentLocation.lat : '',
        longitude: currentLocation ? currentLocation.lng : '',
      },
    },
  );

  useEffect(() => {
    // オファリング一覧取得APIのレスポンス
    if (!isLoading) {
      if (isError) {
        setModalShow(true);
      } else if (offeringsAcceptabilityResponse) {
        // 乗降経路確認画面に遷移する
        navigate(paths.driveRoutes, {
          state: {
            areaId,
            searchType,
            checkedOfferingList,
            driveList: offeringsAcceptabilityResponse?.optimizedRoutes,
          },
        });
      }
    }
  }, [isError, isLoading, offeringsAcceptabilityResponse, areaId, navigate, checkedOfferingList, searchType]);

  // 画面の経路確認ボタン
  const handleClickRouteConfirm = () => {
    setFetchEnable(true);
  };

  useEffect(() => {
    if (checkedOfferingList) {
      const locations = checkedOfferingList?.map((item) => ({
        lat: Number(item.pickupLatitude),
        lng: Number(item.pickupLongitude),
      }));
      const dropoffLocations = checkedOfferingList?.map((item) => ({
        lat: Number(item.droppoffLatitude),
        lng: Number(item.droppoffLongitude),
      }));
      locations.push(...dropoffLocations);

      const { zooms, centers } = calculateMapProperties(locations);
      if (zooms && centers) {
        setCenter(centers);
        setCurrentZoom(zooms);
      }

      const truncateToSixDecimal = (latitudeLongitude: number) => {
        const decimalIndex = String(latitudeLongitude).indexOf('.');
        if (decimalIndex === -1) {
          return latitudeLongitude;
        }

        return Number(String(latitudeLongitude).substring(0, decimalIndex + 7));
      };

      // 初回表示時は、アプリ利用者の現在地を中心に表示
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setCurrentLocation({
            lat: truncateToSixDecimal(position.coords.latitude),
            lng: truncateToSixDecimal(position.coords.longitude),
          });
        },
        () => {},
      );
      // 配車予約ID
      setReservationIds(checkedOfferingList?.map((item) => item.reservationId).join(','));
    }
  }, [checkedOfferingList]);

  // ピンを取得する
  const getMarkerIcon = (isPickup: boolean, waypointId: number, index: number) => {
    const samePointList = checkedOfferingList.filter(
      (item) =>
        waypointId &&
        (String(item.pickupWaypointId) === String(waypointId) || String(item.dropoffWaypointId) === String(waypointId)),
    );
    const pickupWaypoints = samePointList.map((item) => String(item.pickupWaypointId));
    const dropoffWaypoints = samePointList.map((item) => String(item.dropoffWaypointId));

    const numberedBlu = [blu1Png, blu2Png, blu3Png];
    const numberedGrn = [green1Png, green2Png, green3Png];

    let markerIcon = '';
    // 乗車・降車が重なった場合：青緑2色混合ピン（番号なし）
    if (samePointList.length > 1) {
      if (pickupWaypoints.includes(String(waypointId)) && dropoffWaypoints.includes(String(waypointId))) {
        markerIcon = bluGrnSvg.default || '';
      } else if (pickupWaypoints.includes(String(waypointId))) {
        markerIcon = bluSvg.default || '';
      } else if (dropoffWaypoints.includes(String(waypointId))) {
        markerIcon = grnSvg.default || '';
      }
    } else if (samePointList.length <= 1) {
      // 単一地点：番号付きピン（1〜3）
      if (index >= 0 && index <= 2) {
        markerIcon = isPickup ? numberedBlu[index].default || '' : numberedGrn[index].default || '';
      } else {
        markerIcon = isPickup ? bluSvg.default || '' : grnSvg.default || '';
      }
    }

    return markerIcon;
  };

  const renderMarkers = () => {
    // 乗車地点を表示
    const pickupInformationMarkers = checkedOfferingList.map((markerData, index) => (
      <MarkerF
        key={`${markerData?.reservationId}.${markerData?.pickupWaypointId}`}
        position={{ lat: Number(markerData?.pickupLatitude), lng: Number(markerData?.pickupLongitude) }}
        icon={{
          url: getMarkerIcon(true, markerData?.pickupWaypointId, index),
          scaledSize: new google.maps.Size(35, 50),
        }}
      />
    ));

    const dropoffInformationMarkers = checkedOfferingList.map((markerData, index) => (
      <MarkerF
        key={`${markerData?.reservationId}.${markerData?.dropoffWaypointId}`}
        position={{ lat: Number(markerData?.droppoffLatitude), lng: Number(markerData?.droppoffLongitude) }}
        icon={{
          url: getMarkerIcon(false, markerData?.dropoffWaypointId, index),
          scaledSize: new google.maps.Size(35, 50),
        }}
      />
    ));

    return [...pickupInformationMarkers, ...dropoffInformationMarkers].filter(Boolean);
  };

  return (
    <div className="u-flex-fill" style={{ flexDirection: 'column' }}>
      {/* 左右分割レイアウト。表示範囲変更機能のため削除しないこと。 */}
      <div className="drive-routes-split-layout" style={landscapeSplitStyle}>
        <div className="drive-routes-info-panel">
          <div className="drive-routes-info-panel-inner">
            <div className="u-pl-20 u-pr-20 u-py-5 u-border-bottom-strong u-text-20">
              <div>
                {t('waypointConfirm.operationDate')}
                {formatDate(checkedOfferingList?.[0]?.requestedAppointmentAt)}
              </div>
            </div>
            {/* 運行リスト（経路確認前） */}
            {checkedOfferingList?.map((data) => (
              <div key={data?.reservationId} className="waypoint-confirm-card">
                <div className="waypoint-confirm-column">
                  <div className="route-time-row">
                    {/* 乗車時刻を表示、無ければ空欄 */}
                    <span className="route-time">
                      {data?.requestedAppointmentAt ? convertTimeFormat(data?.requestedAppointmentAt) : ''}
                    </span>
                  </div>
                  <div className="waypoint-confirm-row">
                    <span className="status-badge is-pickup">乗</span>
                    <div className="waypoint-confirm-content">
                      <div className="waypoint-tag is-pickup">{data?.pickupWaypointNumber}</div>
                      <Typography className="route-location">
                        {t('driveRoutes.pickup')} {data?.pickupWaypointName}
                      </Typography>
                    </div>
                  </div>
                </div>
                <div className="waypoint-confirm-column">
                  <div className="route-time-row">
                    {/* 降車時刻は表示しないが、レイアウトを揃えるために高さだけ確保 */}
                    <span className="route-time route-time--spacer" aria-hidden="true">
                      {data?.requestedAppointmentAt ? convertTimeFormat(data?.requestedAppointmentAt) : ''}
                    </span>
                  </div>
                  <div className="waypoint-confirm-row">
                    <span className="status-badge is-dropoff">降</span>
                    <div className="waypoint-confirm-content">
                      <div className="waypoint-tag is-dropoff">{data?.dropoffWaypointNumber}</div>
                      <Typography className="route-location">
                        {t('driveRoutes.dropoff')} {data?.dropoffWaypointName}
                      </Typography>
                    </div>
                  </div>
                </div>
              </div>
            ))}
            {/* 経路確認ボタン */}
            <div className="group-waypoint-details">
              <button
                type="button"
                className="primary-action"
                style={{ width: '80%', margin: '0 auto' }}
                disabled={isLoading}
                onClick={() => {
                  handleClickRouteConfirm();
                }}
              >
                {t('waypointConfirm.routeConfirm')}
              </button>
            </div>
            <div className="group-waypoint-details u-py-5">
              <button
                type="button"
                className="secondary-action"
                onClick={() => {
                  navigateToOfferingPage();
                }}
                style={{ width: '80%', margin: '0 auto' }}
              >
                {t('waypointConfirm.cancel')}
              </button>
            </div>
          </div>
        </div>
        {/* 左右リサイズハンドル。表示範囲変更機能のため削除しないこと。 */}
        <button
          type="button"
          className="landscape-resize-handle"
          onMouseDown={() => setIsLandscapeResizing(true)}
          onTouchStart={() => setIsLandscapeResizing(true)}
          aria-label="Resize info and map panels"
        >
          <span className="landscape-resize-handle-bar" />
        </button>
        <Box
          sx={{
            flex: 1,
            overflow: 'hidden',
          }}
        >
          {isStorybook ? (
            <Box
              sx={{
                width: '100%',
                height: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: '#f2f2f2',
                color: '#6f6f6f',
                fontSize: 18,
                fontWeight: 600,
              }}
            >
              Map preview is disabled in Storybook
            </Box>
          ) : (
            center?.lat &&
            center?.lng && (
              <GoogleMapWrapper>
                <MyGoogleMap center={center} zoom={zoom} renderMarkers={renderMarkers} onMarkerClick={() => {}}>
                  {typeof window.google !== 'undefined' &&
                  typeof window.google.maps !== 'undefined' &&
                  typeof window.google.maps.SymbolPath !== 'undefined' &&
                  currentLocation?.lat &&
                  currentLocation.lng ? (
                    <MarkerF
                      key="currentLocation"
                      position={currentLocation}
                      icon={{
                        path: google.maps.SymbolPath.CIRCLE,
                        scale: 12,
                        fillColor: '#4285F4',
                        fillOpacity: 0.9,
                        strokeWeight: 2,
                        strokeColor: 'white',
                      }}
                      zIndex={1}
                      title="現在地"
                    />
                  ) : null}
                </MyGoogleMap>
              </GoogleMapWrapper>
            )
          )}
        </Box>
      </div>
      <MbDialog
        open={modalShow}
        title={t('waypointConfirm.confirmMessageTitle')}
        content={t('waypointConfirm.confirmMessageContent')}
        okLabel={t('waypointConfirm.ok')}
        onOk={() => {
          handleClosePopup();
        }}
        onClose={() => handleClosePopup()}
      />
    </div>
  );
};
